import React, { useEffect, useState, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, Loader2, Check, X, Calendar,
  Users, Clock, AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import UserNav from "../components/UserNav";
import { useUserAuth } from "../context/UserAuthContext";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAYS_MINI = ["L","M","X","J","V","S","D"];

export default function ReservasPage() {
  const { user, logout } = useUserAuth();
  const [trainers, setTrainers] = useState([]);
  const [credits, setCredits] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [currentMonth, setCurrentMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(null); // slot being booked
  const [bookingDone, setBookingDone] = useState(null); // confirmed booking
  const [bookingErr, setBookingErr] = useState("");
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [view, setView] = useState("nueva"); // "nueva" | "mis-reservas"
  const [cancelling, setCancelling] = useState(null);

  const totalCredits = credits.reduce((s, c) => s + Math.max(0, c.creditos_total - c.creditos_usados), 0);

  const loadTrainers = async () => {
    const data = await api.get("/trainers").then(r => r.data).catch(() => []);
    setTrainers(data);
    if (data.length > 0) setSelectedTrainer(data[0]);
  };
  const loadCredits = async () => {
    const data = await api.get("/credits/my-credits").then(r => r.data).catch(() => []);
    setCredits(data);
  };
  const loadMyBookings = async () => {
    setLoadingBookings(true);
    const data = await api.get("/bookings/my-bookings").then(r => r.data).catch(() => []);
    setMyBookings(data);
    setLoadingBookings(false);
  };

  useEffect(() => { loadTrainers(); loadCredits(); loadMyBookings(); }, []);

  const loadAvailableDates = useCallback(async () => {
    if (!selectedTrainer) return;
    setLoadingDates(true);
    setAvailableDates([]);
    setSelectedDate(null);
    setSlots([]);
    try {
      const data = await api.get(`/bookings/available-dates?trainer_id=${selectedTrainer.id}&year=${currentMonth.year}&month=${currentMonth.month}`).then(r => r.data);
      setAvailableDates(data);
    } finally { setLoadingDates(false); }
  }, [selectedTrainer, currentMonth]);

  useEffect(() => { if (view === "nueva") loadAvailableDates(); }, [loadAvailableDates, view]);

  const handleDateClick = async (dateStr) => {
    setSelectedDate(dateStr);
    setSlots([]);
    setLoadingSlots(true);
    try {
      const data = await api.get(`/bookings/slots?trainer_id=${selectedTrainer.id}&fecha=${dateStr}`).then(r => r.data);
      setSlots(data);
    } finally { setLoadingSlots(false); }
  };

  const handleBook = async () => {
    if (!booking) return;
    setBookingErr("");
    setBookingDone(null);
    try {
      const result = await api.post("/bookings", { trainer_id: selectedTrainer.id, fecha_hora: booking.fecha_hora }).then(r => r.data);
      setBookingDone(result);
      setBooking(null);
      await loadCredits();
      await loadMyBookings();
      await loadAvailableDates();
    } catch (e) {
      setBookingErr(formatApiError(e?.response?.data?.detail) || "Error al reservar");
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm("¿Cancelar esta reserva? Recibirás el crédito de vuelta.")) return;
    setCancelling(bookingId);
    try {
      await api.delete(`/bookings/${bookingId}`);
      await loadMyBookings();
      await loadCredits();
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail) || "No se pudo cancelar");
    } finally { setCancelling(null); }
  };

  // Calendar grid
  const firstDay = new Date(currentMonth.year, currentMonth.month - 1, 1).getDay();
  const startOffset = (firstDay + 6) % 7; // Mon=0
  const daysInMonth = new Date(currentMonth.year, currentMonth.month, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);

  const prevMonth = () => setCurrentMonth(m => m.month === 1 ? { year: m.year - 1, month: 12 } : { ...m, month: m.month - 1 });
  const nextMonth = () => setCurrentMonth(m => m.month === 12 ? { year: m.year + 1, month: 1 } : { ...m, month: m.month + 1 });

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body" data-testid="reservas-page">
      <UserNav user={user} onLogout={logout} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Credits banner */}
        <div className={`border ${totalCredits > 0 ? "border-[#FFCC00]/25 bg-[#FFCC00]/3" : "border-red-500/20 bg-red-500/5"} px-5 py-4 mb-6 flex items-center justify-between`} data-testid="credits-banner">
          <div>
            <p className="text-[9px] tracking-[0.25em] uppercase font-black text-white/40 mb-0.5">Sesiones disponibles</p>
            <p className={`font-display text-3xl ${totalCredits > 0 ? "text-[#FFCC00]" : "text-red-400"}`}>{totalCredits}</p>
          </div>
          {totalCredits === 0 && (
            <a href="/tienda" className="text-[10px] font-black tracking-[0.2em] uppercase px-4 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">
              Comprar bono
            </a>
          )}
        </div>

        {/* View toggle */}
        <div className="flex border border-white/10 mb-6 w-fit">
          <button onClick={() => setView("nueva")} data-testid="tab-nueva-reserva"
            className={`px-4 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "nueva" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
            Nueva reserva
          </button>
          <button onClick={() => setView("mis-reservas")} data-testid="tab-mis-reservas"
            className={`px-4 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${view === "mis-reservas" ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
            Mis reservas ({myBookings.filter(b => b.estado === "confirmada").length})
          </button>
        </div>

        {/* ── NUEVA RESERVA ── */}
        {view === "nueva" && (
          <>
            {bookingDone && (
              <div className="mb-6 border border-green-500/30 bg-green-500/8 p-4 flex items-start gap-3" data-testid="booking-success">
                <Check size={18} className="text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-black text-sm text-green-400">¡Reserva confirmada!</p>
                  <p className="text-xs text-white/50 mt-0.5">{bookingDone.trainer_nombre} · {bookingDone.fecha_hora?.replace("T", " ").slice(0, 16)}</p>
                </div>
                <button onClick={() => setBookingDone(null)} className="ml-auto text-white/25 hover:text-white"><X size={14} /></button>
              </div>
            )}

            {/* Trainer picker */}
            {trainers.length > 0 && (
              <div className="mb-6">
                <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">Entrenador</p>
                <div className="flex flex-wrap gap-2">
                  {trainers.map(t => (
                    <button key={t.id} onClick={() => setSelectedTrainer(t)} data-testid={`trainer-btn-${t.id}`}
                      className={`flex items-center gap-2 px-3 py-2 border text-sm font-bold transition-colors ${selectedTrainer?.id === t.id ? "border-[#FFCC00] bg-[#FFCC00]/8 text-white" : "border-white/10 text-white/50 hover:border-white/30 hover:text-white"}`}>
                      {t.foto_url && <img src={t.foto_url} alt={t.nombre} className="w-6 h-6 rounded-full object-cover" />}
                      {t.nombre}
                    </button>
                  ))}
                </div>
                {selectedTrainer?.especialidad && (
                  <p className="text-[11px] text-white/35 mt-2">{selectedTrainer.especialidad}</p>
                )}
              </div>
            )}

            {/* Calendar */}
            <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-1.5 text-white/40 hover:text-white transition-colors"><ChevronLeft size={16} /></button>
                <p className="text-sm font-black tracking-wider">{MONTHS_ES[currentMonth.month - 1]} {currentMonth.year}</p>
                <button onClick={nextMonth} className="p-1.5 text-white/40 hover:text-white transition-colors"><ChevronRight size={16} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_MINI.map(d => <p key={d} className="text-center text-[10px] font-black text-white/25 uppercase">{d}</p>)}
              </div>
              {loadingDates ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#FFCC00]" /></div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: startOffset }).map((_, i) => <div key={`e${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const dateStr = `${currentMonth.year}-${String(currentMonth.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const isAvailable = availableDates.includes(dateStr);
                    const isSelected = selectedDate === dateStr;
                    const isPast = dateStr < today;
                    return (
                      <button key={day} onClick={() => isAvailable && handleDateClick(dateStr)}
                        disabled={!isAvailable || isPast} data-testid={`cal-day-${dateStr}`}
                        className={`aspect-square flex items-center justify-center text-xs font-bold rounded-sm transition-colors ${
                          isSelected ? "bg-[#FFCC00] text-black" :
                          isAvailable ? "bg-[#FFCC00]/10 text-[#FFCC00] hover:bg-[#FFCC00]/20 cursor-pointer" :
                          isPast ? "text-white/15 cursor-not-allowed" : "text-white/25 cursor-not-allowed"
                        }`}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Slots */}
            {selectedDate && (
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">
                  Horarios disponibles — {selectedDate}
                </p>
                {loadingSlots ? <Spinner /> : slots.length === 0 ? (
                  <p className="text-white/30 text-sm">No hay horarios disponibles para este día.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slots-grid">
                    {slots.map(s => (
                      <button key={s.hora} onClick={() => { if (s.disponible && totalCredits > 0) { setBooking(s); setBookingErr(""); } }}
                        disabled={!s.disponible || totalCredits === 0} data-testid={`slot-${s.hora}`}
                        className={`py-3 text-sm font-black border transition-colors ${
                          !s.disponible ? "border-white/5 text-white/15 cursor-not-allowed" :
                          totalCredits === 0 ? "border-white/10 text-white/25 cursor-not-allowed" :
                          "border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black cursor-pointer"
                        }`}>
                        {s.hora}
                        {!s.disponible && <span className="block text-[9px] text-white/20">Ocupado</span>}
                      </button>
                    ))}
                  </div>
                )}
                {totalCredits === 0 && slots.some(s => s.disponible) && (
                  <p className="mt-3 flex items-center gap-2 text-xs text-red-400">
                    <AlertCircle size={13} /> Necesitas créditos para reservar.
                    <a href="/tienda" className="underline">Comprar bono</a>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* ── MIS RESERVAS ── */}
        {view === "mis-reservas" && (
          <div data-testid="mis-reservas-list">
            {loadingBookings ? <Spinner /> : myBookings.length === 0 ? (
              <div className="border border-white/8 p-12 text-center">
                <Calendar size={32} className="mx-auto text-white/10 mb-3" />
                <p className="text-white/30 text-sm">No tienes reservas todavía.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {myBookings.map(b => {
                  const isPast = b.fecha_hora < new Date().toISOString();
                  return (
                    <div key={b.id} className={`border ${b.estado === "confirmada" && !isPast ? "border-white/10" : "border-white/5 opacity-60"} bg-[#0a0a0a] px-4 py-3 flex items-center justify-between gap-3`} data-testid={`my-booking-${b.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 border ${b.estado === "confirmada" ? "border-green-500/30 text-green-400" : "border-white/10 text-white/30"}`}>{b.estado}</span>
                        </div>
                        <p className="text-sm font-black text-white">{b.trainer_nombre}</p>
                        <p className="text-[11px] text-white/40">{b.fecha_hora?.replace("T", " ").slice(0, 16)} · {b.duracion_minutos} min</p>
                      </div>
                      {b.estado === "confirmada" && !isPast && (
                        <button onClick={() => handleCancel(b.id)} disabled={cancelling === b.id}
                          data-testid={`cancel-booking-${b.id}`}
                          className="px-3 py-1.5 border border-red-500/25 text-red-400/70 hover:border-red-500/50 hover:text-red-400 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 shrink-0">
                          {cancelling === b.id ? <Loader2 size={11} className="animate-spin" /> : "Cancelar"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Booking confirmation modal */}
      {booking && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-testid="booking-confirm-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setBooking(null)} />
          <div className="relative w-full max-w-sm bg-[#0d0d0d] border border-white/10 shadow-2xl p-6">
            <p className="text-[10px] tracking-[0.25em] uppercase font-black text-[#FFCC00] mb-3">Confirmar reserva</p>
            <div className="space-y-2 mb-5">
              <div className="flex items-center gap-2 text-sm">
                <Users size={13} className="text-white/40" />
                <span className="text-white font-bold">{selectedTrainer?.nombre}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={13} className="text-white/40" />
                <span className="text-white/70">{selectedDate}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock size={13} className="text-white/40" />
                <span className="text-white/70">{booking.hora} · {booking.duracion_minutos} min</span>
              </div>
            </div>
            <p className="text-[11px] text-white/35 mb-5 border border-white/8 px-3 py-2">
              Se consumirá 1 crédito de sesión de tu saldo ({totalCredits} disponible{totalCredits !== 1 ? "s" : ""}).
            </p>
            {bookingErr && <p className="text-red-400 text-xs mb-4" data-testid="booking-error">{bookingErr}</p>}
            <div className="flex gap-2">
              <button onClick={handleBook} data-testid="confirm-booking-btn"
                className="flex-1 py-3 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
                Confirmar
              </button>
              <button onClick={() => setBooking(null)} className="px-4 border border-white/15 text-white/40 text-[11px] font-bold uppercase tracking-wider hover:text-white transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner() { return <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-[#FFCC00]" /></div>; }
